import { maybe, rd } from '@passionware/monads';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import type { ResolvedAppScope } from 'v5/core/app-scope/appScope.types';
import {
  teamIdFromSettingsPath,
  workspaceIdFromSettingsPath
} from 'v5/core/app-scope/resolveSettingsAppScopeFromPathname';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseNewTeamMenuItem } from 'v5/features/core/enterprise/EnterpriseNewTeamMenuItem';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { renderEnterpriseTeamSettingsItems } from 'v5/features/core/enterprise/renderEnterpriseTeamSettingsItems';
import { renderEnterpriseWorkspaceSettingsItems } from 'v5/features/core/enterprise/renderEnterpriseWorkspaceSettingsItems';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import {
  MenuCollapsible,
  MenuItem,
  MenuSeparator,
  menuPaddingLeft
} from 'v5/patterns/sidebar/Menu';
import { SidebarSectionHeader } from 'v5/patterns/sidebar/Sidebar';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';

export type EnterpriseWorkspaceSettingsCollapsibleProps = WithServices<
  [WithEnterpriseService, WithUserFlowService]
> & {
  workspace: EnterpriseWorkspace;
  organizationId: string;
  scope: ResolvedAppScope | undefined;
  canManage?: boolean;
  canConfigureResourceTypes?: boolean;
};

export function EnterpriseWorkspaceSettingsCollapsible({
  services,
  workspace,
  organizationId,
  scope,
  canManage = false,
  canConfigureResourceTypes = true
}: EnterpriseWorkspaceSettingsCollapsibleProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { t: tResourceTypes } = useTranslation('core', {
    keyPrefix: 'resourceTypesAdmin'
  });
  const { pathname } = useLocation();
  const settingsWorkspaceId = workspaceIdFromSettingsPath(pathname);
  const settingsTeamId = teamIdFromSettingsPath(pathname);

  const routing = appRouting.org(organizationId);
  const wsSettings = routing.settings.workspace(workspace.slug);

  // Only auto-expand when browsing that workspace's settings (`/settings/w/...`).
  // Do not expand from the org-level “default workspace for links” fallback — that
  // makes Global settings pages open a huge tree and the sidebar height jump.
  const isActiveWorkspace =
    settingsWorkspaceId != null && workspace.slug === settingsWorkspaceId;

  const isInWorkspaceSettings = settingsWorkspaceId === workspace.slug;
  const [isOpen, setIsOpen] = useState(isActiveWorkspace);

  const teamsRd = services.enterpriseService.useTeams(
    isInWorkspaceSettings || isOpen
      ? maybe.of({
          organizationId,
          workspaceId: workspace.id
        })
      : maybe.ofAbsent()
  );

  const handleCreateTeam = () => {
    services.userFlowService.openCreateEnterpriseTeamDialog({
      organizationId,
      workspaceId: workspace.id
    });
  };

  return (
    <MenuCollapsible
      icon={
        <EnterpriseWorkspaceIcon
          name={workspace.name}
          iconUrl={workspace.iconUrl}
        />
      }
      label={workspace.name}
      defaultOpen={isActiveWorkspace}
      expandOnChildSelect
      onOpenChange={setIsOpen}
    >
      {renderEnterpriseWorkspaceSettingsItems(wsSettings, 1, {
        canConfigureResourceTypes,
        resourceTypesLabel: tResourceTypes('workspaceTitle')
      })}

      <MenuSeparator level={1} className="my-1 mx-3" />
      <SidebarSectionHeader level={1}>
        {t('teamsSettingsLabel')}
      </SidebarSectionHeader>

      {rd
        .journey(teamsRd)
        .wait(<EnterpriseMenuCollapsibleSkeleton count={2} level={1} />)
        .catch(() => (
          <div
            className="py-2 pr-3 text-[13px] text-fg-muted"
            style={{ paddingLeft: menuPaddingLeft(1) }}
          >
            {t('teamsUnavailable')}
          </div>
        ))
        .map(teams =>
          teams.length === 0 ? (
            <EnterpriseSidebarEmptyState
              messageKey="emptyTeams"
              level={1}
              className="py-1"
            />
          ) : (
            teams.map(team => (
              <MenuCollapsible
                key={team.id}
                level={1}
                icon={
                  <EnterpriseTeamIcon
                    name={team.name}
                    iconUrl={team.iconUrl}
                    iconColor={team.iconColor}
                  />
                }
                label={team.name}
                defaultOpen={
                  isActiveWorkspace &&
                  (settingsTeamId === team.slug ||
                    (settingsTeamId == null &&
                      scope?.level === 'team' &&
                      scope.teamSlug === team.slug))
                }
                expandOnChildSelect
              >
                {renderEnterpriseTeamSettingsItems(
                  team,
                  wsSettings.team(team.slug)
                )}
              </MenuCollapsible>
            ))
          )
        )}

      {canManage ? (
        <EnterpriseNewTeamMenuItem onClick={handleCreateTeam} />
      ) : (
        <MenuItem
          level={1}
          disabled
          className="text-fg-muted"
          icon={<Plus className="size-4" />}
        >
          {t('newTeam')}
        </MenuItem>
      )}
    </MenuCollapsible>
  );
}
